import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import styles from "./CoordinatesForm.module.css";

function CoordinatesForm() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [coordinatesText, setCoordinatesText] = useState("");
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    "Координаты успешно отправлены"
  );

  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const clearError = () => {
    setErrorTitle("");
    setErrorMessage("");
  };

  useEffect(() => {
    if (!currentUser) navigate("/");
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!successOpen) return;
    const t = setTimeout(() => navigate("/dashboard"), 3000);
    return () => clearTimeout(t);
  }, [successOpen, navigate]);

  const isFormValid = useMemo(() => {
    return String(coordinatesText).trim().length > 0;
  }, [coordinatesText]);

  async function safeReadResponse(response) {
    const ct = response.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        return await response.json();
      } catch {
        return {};
      }
    }

    try {
      const text = await response.text();
      return { details: text };
    } catch {
      return {};
    }
  }

  function normalizeErrorMessage(status, data) {
    const raw =
      data?.details ||
      data?.error ||
      data?.message ||
      (typeof data === "string" ? data : "") ||
      "";

    if (status === 403) return raw || "Недостаточно прав";
    if (status === 401) return raw || "Требуется авторизация";
    if (status >= 500) return raw || "Ошибка сервера";
    return raw || "Ошибка запроса";
  }

  const handleUpdateCoordinates = async () => {
    clearError();

    if (!navigator.geolocation) {
      setErrorTitle("Геолокация недоступна");
      setErrorMessage(
        "Ваш браузер не поддерживает определение местоположения."
      );
      return;
    }

    try {
      setLoadingLocation(true);

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        });
      });

      const { latitude, longitude, accuracy } = position.coords;
      const capturedAt = new Date().toLocaleString("ru-RU");

      const nextText = [
        `Широта: ${latitude}`,
        `Долгота: ${longitude}`,
        `Точность: ${Math.round(accuracy)} м`,
        `Время: ${capturedAt}`,
      ].join("\n");

      setCoordinatesText(nextText);
    } catch (err) {
      let message = "Не удалось получить координаты.";

      if (err?.code === 1) {
        message = "Доступ к геолокации запрещён пользователем.";
      } else if (err?.code === 2) {
        message = "Не удалось определить местоположение.";
      } else if (err?.code === 3) {
        message = "Истекло время ожидания геолокации.";
      }

      setErrorTitle("Ошибка геолокации");
      setErrorMessage(message);
    } finally {
      setLoadingLocation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    setSuccessOpen(false);

    if (!isFormValid || submitting) {
      setErrorTitle("Проверьте поля");
      setErrorMessage("Сначала получите или введите координаты.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        coordinates: coordinatesText,
      };

      const resp = await fetch("/api/coordinates/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await safeReadResponse(resp);

      if (resp.ok) {
        const msg =
          data?.details || data?.message || "Координаты успешно отправлены";
        setSuccessMessage(typeof msg === "string" ? msg : JSON.stringify(msg));
        setSuccessOpen(true);
      } else {
        setErrorTitle("Не удалось отправить");
        setErrorMessage(normalizeErrorMessage(resp.status, data));
      }
    } catch (err) {
      setErrorTitle("Ошибка сети");
      setErrorMessage(
        "Не удалось связаться с сервером. " + (err?.message || "")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentUser) return <div>Загрузка...</div>;

  return (
    <div className={styles.container}>
      {successOpen && (
        <div className={styles.successOverlay} role="dialog" aria-modal="true">
          <div className={styles.successModal}>
            <div className={styles.successIcon} aria-hidden="true" />
            <h3>Успешно!</h3>
            <p>{successMessage}</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} />
            </div>
            <p className={styles.redirectText}>
              Переход в кабинет через 3 секунды...
            </p>
          </div>
        </div>
      )}

      <div className={styles.formContainer}>
        <div className={styles.header}>
          <h2>Отправка координат</h2>
          <button
            onClick={() => navigate("/dashboard")}
            className={styles.backButton}
            type="button"
          >
            Назад
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputField}>
            <label className={styles.label} htmlFor="coordinatesText">
              Текущие координаты:
            </label>
            <textarea
              id="coordinatesText"
              className={`${styles.input} ${styles.textarea}`}
              value={coordinatesText}
              placeholder="Нажмите «Обновить координаты»"
              readOnly
              disabled={submitting}
            />
          </div>

          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleUpdateCoordinates}
            disabled={loadingLocation || submitting}
          >
            {loadingLocation ? "Получение координат..." : "Обновить координаты"}
          </button>

          {(errorTitle || errorMessage) && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              <div>
                <p className={styles.alertText}>{errorTitle || "Ошибка"}</p>
                {errorMessage && (
                  <p className={styles.alertSubText}>{errorMessage}</p>
                )}
              </div>
              <button
                type="button"
                className={styles.alertButton}
                onClick={clearError}
              >
                Понятно
              </button>
            </div>
          )}

          <button
            type="submit"
            className={styles.sendButton}
            disabled={!isFormValid || submitting || loadingLocation}
          >
            {submitting ? "Отправка..." : "Отправить координаты"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CoordinatesForm;
